import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@/core';
import { notify } from '@/shared/services';
import { settleGuestCharge, createGuestChargeSellable } from '../services/guestService';
import type { GuestChargeSettleInput, GuestChargeSellableInput } from '../types';

export const guestChargeKeys = {
  all: ['booking-guest-charges'] as const,
  byBooking: (bookingId: string | null) =>
    [...guestChargeKeys.all, bookingId] as const,
};

/**
 * Invalidate every cache key reading guest charges. ``PersonProfilePage``
 * keeps its own ``['person-guest-charges', <bookingIds>]`` query whose
 * second segment is the joined booking-id string — we don't know the
 * exact joined value here, so we invalidate by predicate (match any
 * query whose first key segment starts with ``person-guest-charges``).
 *
 * Without this sweep, settling or creating a charge re-renders only
 * the booking-scoped surface; the per-person panel stays stale until
 * the user reloads the page.
 *
 * ``['folio', bookingId]`` is in the sweep because
 * ``POST /guest-charges/charge-sellable/`` writes a **FolioCharge**, not a
 * GuestCharge. The folio balance is what the booking's payment flow collects,
 * so leaving it cached means the operator pays the pre-charge amount.
 */
function invalidateChargeQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  bookingId: string,
) {
  queryClient.invalidateQueries({ queryKey: guestChargeKeys.byBooking(bookingId) });
  queryClient.invalidateQueries({ queryKey: ['booking-expense-statement', bookingId] });
  queryClient.invalidateQueries({ queryKey: ['folio', bookingId] });
  queryClient.invalidateQueries({
    predicate: (q) => {
      const head = Array.isArray(q.queryKey) ? q.queryKey[0] : null;
      return (
        head === 'person-guest-charges' ||
        head === 'person-guest-folio-charges' ||
        head === 'person-guest-deposits'
      );
    },
  });
}

export function useSettleGuestCharge(bookingId: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ chargeId, input }: { chargeId: string; input: GuestChargeSettleInput }) =>
      settleGuestCharge(chargeId, input),
    onSuccess: () => {
      invalidateChargeQueries(queryClient, bookingId);
      queryClient.invalidateQueries({ queryKey: ['booking-guest-deposits', bookingId] });
      notify.success(t('guestCharges.settled', 'Charge settled successfully.'));
    },
    onError: (error: Error) => {
      notify.error(error.message, {
        description: t('guestCharges.settleFailed', 'Could not settle the charge.'),
      });
    },
  });
}

export function useCreateGuestChargeSellable(bookingId: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (input: GuestChargeSellableInput) => createGuestChargeSellable(input),
    onSuccess: () => {
      invalidateChargeQueries(queryClient, bookingId);
      notify.success(t('guestCharges.sellableCreated', 'Charge added successfully.'));
    },
    onError: (error: Error) => {
      // El tope de folio no es un fallo: es una pregunta. El diálogo la
      // recoge y pide la autorización, así que un toast rojo aquí contaría
      // una avería que no ha pasado.
      if (readFolioCapRefusal(error)) return;
      notify.error(error.message, {
        description: t('guestCharges.sellableCreateFailed', 'Could not add the charge.'),
      });
    },
  });
}

/** Lo que el servidor contesta cuando el cargo pasa del tope de la sede. */
export interface FolioCapRefusal {
  /** `required` se puede autorizar; `exceeded` no lo permite la sede. */
  kind: 'required' | 'exceeded';
  resultingTotal: string;
  cap: string;
  detail: string;
}

/**
 * Lee el código, no el mensaje.
 *
 * Pasarse del tope con autorización posible y pasarse donde la sede no la
 * admite son dos respuestas distintas con una frase cada una; distinguirlas
 * por la prosa se rompe el día que alguien reescribe el texto.
 */
export function readFolioCapRefusal(error: unknown): FolioCapRefusal | null {
  const data = (error as { data?: unknown })?.data as
    | { code?: string; detail?: string; resultingTotal?: string; cap?: string }
    | undefined;
  if (!data || typeof data !== 'object') return null;
  if (data.code !== 'folio_cap_override_required' && data.code !== 'folio_cap_exceeded') {
    return null;
  }
  return {
    kind: data.code === 'folio_cap_override_required' ? 'required' : 'exceeded',
    resultingTotal: data.resultingTotal ?? '',
    cap: data.cap ?? '',
    detail: data.detail ?? '',
  };
}
